import { Theme, themeQuartz } from 'ag-grid-community';
import { theme } from 'antd';
import { createStyles } from 'antd-style';
import { useMemo } from 'react';
import { colors } from 'theme/colors';

import { isAutoTotalRow } from 'utils/revenue/helpers';

export function useTableTheme() {
  const { token } = theme.useToken();
  const agGridTheme = useMemo<Theme>(
    () =>
      themeQuartz.withParams({
        accentColor: token.colorPrimary,
        fontFamily: token.fontFamily,
        fontSize: token.fontSize,
        headerFontWeight: token.fontWeightStrong,
        headerBackgroundColor: colors.mauveGrey1,
        backgroundColor: token.colorBgContainer,
        iconColor: token.colorText,
        borderColor: token.colorBorder,
        rowLoadingSkeletonEffectColor: token.colorBgElevated,
        selectedRowBackgroundColor: token.colorPrimaryBg,
        valueChangeValueHighlightBackgroundColor: token.colorPrimaryBg,
        rowHoverColor: colors.colorPrimaryBg,
      }),
    [token]
  );
  return { agGridTheme };
}

const useTableStyles = createStyles(({ token }) => ({
  agGrid: {
    fontFeatureSettings: '"tnum" 1',
    '& .dataCell': {
      borderRight: `1px solid ${token.colorBorder} !important`,
    },
    '& .ag-row-group': {
      backgroundColor: colors.mauveGrey1,
      color: token.colorText,
      '&:hover': {
        backgroundColor: colors.colorPrimaryBg,
      },
    },
    // Alternating row background color (exclude hover and selected states)
    '& .ag-row.row-alt:not(.group-hover):not(.ag-row-hover):not(.ag-row-selected) .ag-cell':
      {
        backgroundColor: 'rgba(247, 245, 247) !important', // MauveGray 1 #F7F5F7
      },
    // Ensure selected rows show selection highlight
    '& .ag-row.ag-row-selected .ag-cell': {
      backgroundColor: `${token.colorPrimaryBg} !important`,
    },
    // Show row group drag handles and remove buttons for full control
    '& .ag-column-drop-rowgroup .ag-column-drop-cell-drag-handle': {
      display: 'block',
    },
    '& .ag-column-drop-rowgroup .ag-column-drop-cell-button': {
      display: 'block',
    },
    // Remove default popup editor styling (custom editors handle their own)
    '& .ag-popup-editor': {
      boxShadow: 'none',
      background: 'transparent',
      border: 'none',
    },
    // Zoom affects the entire table content
    '&.zoom-table': {
      transform: 'var(--table-zoom-scale)',
      transformOrigin: 'top left',
    },
    // Show fullscreen button on table hover
    '&:hover .fullscreen-button': {
      opacity: '1 !important',
    },
    // Hover-to-show row checkboxes
    '&.ag-mx-hide-row-checkboxes': {
      '.ag-row .ag-selection-checkbox': {
        opacity: 0,
        transition: 'opacity 150ms ease-in-out',
      },
      '.ag-row-hover, .ag-row-selected, .ag-header-cell': {
        '.ag-selection-checkbox': {
          opacity: 1,
        },
      },
      '&.ag-mx-show-group-checkbox .ag-row-group .ag-selection-checkbox': {
        opacity: 1,
      },
    },
    // Reorder group row checkbox to appear before expand/collapse arrow
    '&.ag-mx-checkbox-before-arrow .ag-row-group': {
      '.ag-group-checkbox': { order: 1 },
      '.ag-group-expanded, .ag-group-contracted': { order: 2 },
      '.ag-group-value': { order: 3 },
      '.ag-group-child-count': { order: 4 },
    },
  },
}));

export function getRowClass(
  params: any,
  options?: { alternateRows?: boolean }
): string {
  const classes: string[] = [];

  // Handle grand total rows (auto-generated by AG Grid)
  if (isAutoTotalRow(params.node)) {
    return 'auto-total-row';
  }

  // Handle total rows (manual)
  const name =
    params.data?.name || params.data?.line_item_name || params.value || '';
  if (name?.toUpperCase()?.includes('TOTAL')) {
    return 'total-row';
  }

  // Alternating row colors (zebra striping) - only when enabled
  // Start with grey on first row (even indices get grey)
  if (options?.alternateRows) {
    const rowIndex = params.node?.rowIndex ?? 0;
    if (rowIndex % 2 === 0) {
      classes.push('row-alt');
    }
  }

  return classes.join(' ');
}

function withCellStyle(cols: any[]): any[] {
  return cols.map((col) => {
    if ('children' in col && Array.isArray(col.children)) {
      return { ...col, children: withCellStyle(col.children) };
    }
    if ('field' in col) {
      return { ...col };
    }
    return col;
  });
}

export { useTableStyles, withCellStyle };
